// lightweight fetch wrapper; same behavior we outlined before
import { ensureCsrfToken } from "../utils/csrf";
import { ensureIdempotencyHeader } from "./idempotency";

export class HttpError extends Error {
  status: number;
  data: any;
  constructor(message: string, status: number, data: any) {
    super(message);
    this.name = "HttpError";
    this.status = status;
    this.data = data;
  }
}

function getCsrfTokenFromMeta(): string | null {
  if (typeof document === "undefined") return null;
  const meta = document.querySelector(
    'meta[name="csrf-token"], meta[name="mc-csrf"]'
  ) as HTMLMetaElement | null;
  return meta?.content || null;
}

export async function apiFetch<T = any>(
  url: string,
  options: RequestInit & { json?: any; idempotencyKey?: string } = {}
): Promise<T> {
  const { json, idempotencyKey, ...rest } = options;
  const init: RequestInit = {
    credentials: "include",
    ...rest,
    headers: {
      Accept: "application/json",
      ...(options.headers || {}),
    },
  };

  const method = (init.method || "GET").toUpperCase();
  const unsafe = !["GET", "HEAD", "OPTIONS"].includes(method);
  const sameOrigin =
    typeof window === "undefined" ||
    url.startsWith("/") ||
    url.startsWith(window.location.origin);

  if (unsafe && sameOrigin) {
    let token = getCsrfTokenFromMeta();
    if (!token) {
      token = await ensureCsrfToken();
    }
    if (token) {
      (init.headers as any)["X-CSRF-Token"] = token;
    }

    // Inject Idempotency-Key for write operations
    if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
      const { headers: withKey } = ensureIdempotencyHeader(
        init.headers,
        idempotencyKey
      );
      init.headers = withKey;
    }
  }

  if (json !== undefined) {
    (init.headers as any)["Content-Type"] = "application/json";
    init.body = JSON.stringify(json);
  }

  const res = await fetch(url, init);
  const contentType = res.headers.get("Content-Type") || "";
  const isJson = contentType.includes("application/json");
  const data = isJson ? await res.json().catch(() => null) : await res.text();

  if (!res.ok) {
    const message =
      (isJson && data && (data.message || data.error || data.detail)) ||
      `Request failed with status ${res.status}`;
    throw new HttpError(message, res.status, data);
  }

  return data as T;
}
